import React, { useEffect, useMemo, useState } from 'react';
import { Percent, ChevronDown, ChevronRight, Users, Package, Box, Search } from 'lucide-react';
import { Pagination } from '../../components/admin/Pagination';
import {
  adminPricingService,
  ClientePrezziPersonalizzati,
  ArticoloPrezziPersonalizzati,
  ClienteClasseSconto,
  ArticoloScontoScatola,
} from '../../services/adminPricingService';

type Tab = 'clienti-prezzi' | 'articoli-prezzi' | 'clienti-classi' | 'articoli-scatola';

const PAGE_SIZE = 15;

const TABS: { key: Tab; label: string; icon: any }[] = [
  { key: 'clienti-prezzi', label: 'Clienti — Prezzi Personalizzati', icon: Users },
  { key: 'articoli-prezzi', label: 'Articoli — Prezzi Personalizzati', icon: Package },
  { key: 'clienti-classi', label: 'Clienti — Classi di Sconto', icon: Percent },
  { key: 'articoli-scatola', label: 'Articoli — Sconti Scatola', icon: Box },
];

function fmtPrice(n: number | undefined | null) {
  if (n == null) return '—';
  return `€ ${Number(n).toFixed(2).replace('.', ',')}`;
}
function fmtPct(n: number | undefined | null) {
  if (!n) return '—';
  return `${Number(n).toFixed(1).replace('.', ',')}%`;
}
function fmtDate(iso?: string) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (d.getUTCFullYear() >= 2099) return 'Senza scadenza';
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
}

export default function AdminPricingOverviewPage() {
  const [tab, setTab] = useState<Tab>('clienti-prezzi');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [clientiPrezzi, setClientiPrezzi] = useState<ClientePrezziPersonalizzati[]>([]);
  const [articoliPrezzi, setArticoliPrezzi] = useState<ArticoloPrezziPersonalizzati[]>([]);
  const [clientiClassi, setClientiClassi] = useState<ClienteClasseSconto[]>([]);
  const [articoliScatola, setArticoliScatola] = useState<ArticoloScontoScatola[]>([]);

  useEffect(() => {
    setLoading(true);
    setExpanded(new Set());
    setSearch('');
    setPage(1);
    const loaders: Record<Tab, () => Promise<void>> = {
      'clienti-prezzi': async () => setClientiPrezzi((await adminPricingService.clientiPrezziPersonalizzati()).items),
      'articoli-prezzi': async () => setArticoliPrezzi((await adminPricingService.articoliPrezziPersonalizzati()).items),
      'clienti-classi': async () => setClientiClassi((await adminPricingService.clientiClassiSconto()).items),
      'articoli-scatola': async () => setArticoliScatola((await adminPricingService.articoliScontiScatola()).items),
    };
    loaders[tab]().finally(() => setLoading(false));
  }, [tab]);

  // Il filtro cambia il totale risultati: si torna sempre a pagina 1.
  useEffect(() => { setPage(1); }, [search]);

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Filtri lato client (richiesta utente 26/02/2026): i dati per tab sono già
  // caricati per intero, quindi il filtro testuale è istantaneo, senza chiamate
  // aggiuntive al backend.
  const searchNorm = search.trim().toLowerCase();
  const clientiPrezziFiltered = useMemo(() => {
    if (!searchNorm) return clientiPrezzi;
    return clientiPrezzi.filter((c) =>
      c.ragioneSociale?.toLowerCase().includes(searchNorm) || String(c.idCliente).includes(searchNorm)
    );
  }, [clientiPrezzi, searchNorm]);
  const articoliPrezziFiltered = useMemo(() => {
    if (!searchNorm) return articoliPrezzi;
    return articoliPrezzi.filter((a) => a.sku?.toLowerCase().includes(searchNorm));
  }, [articoliPrezzi, searchNorm]);
  const clientiClassiFiltered = useMemo(() => {
    if (!searchNorm) return clientiClassi;
    return clientiClassi.filter((c) =>
      c.ragioneSociale?.toLowerCase().includes(searchNorm) || String(c.idCliente).includes(searchNorm)
    );
  }, [clientiClassi, searchNorm]);

  // Paginazione lato client (richiesta utente 26/02/2026, stesso pattern
  // numerato di AdminClientsPage/AdminRegistrationsPage) — applicata dopo
  // il filtro testuale, così i due si combinano correttamente.
  const listByTab: Record<Tab, any[]> = {
    'clienti-prezzi': clientiPrezziFiltered,
    'articoli-prezzi': articoliPrezziFiltered,
    'clienti-classi': clientiClassiFiltered,
    'articoli-scatola': articoliScatola,
  };
  const currentList = listByTab[tab];
  const pages = Math.max(1, Math.ceil(currentList.length / PAGE_SIZE));
  const pageSafe = Math.min(page, pages);
  const pageStart = (pageSafe - 1) * PAGE_SIZE;
  const clientiPrezziPage: ClientePrezziPersonalizzati[] = tab === 'clienti-prezzi' ? clientiPrezziFiltered.slice(pageStart, pageStart + PAGE_SIZE) : [];
  const articoliPrezziPage: ArticoloPrezziPersonalizzati[] = tab === 'articoli-prezzi' ? articoliPrezziFiltered.slice(pageStart, pageStart + PAGE_SIZE) : [];
  const clientiClassiPage: ClienteClasseSconto[] = tab === 'clienti-classi' ? clientiClassiFiltered.slice(pageStart, pageStart + PAGE_SIZE) : [];
  const articoliScatolaPage: ArticoloScontoScatola[] = tab === 'articoli-scatola' ? articoliScatola.slice(pageStart, pageStart + PAGE_SIZE) : [];

  return (
    <div className="p-6" data-testid="admin-pricing-overview-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Percent className="w-5 h-5 text-[hsl(206,54%,20%)]" /> Condizioni Commerciali
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Prezzi personalizzati, classi di sconto e sconti scatola sincronizzati dal gestionale.
        </p>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b flex-wrap" data-testid="pricing-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`pricing-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              tab === t.key ? 'border-red-500 text-[hsl(206,54%,20%)]' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {(tab === 'clienti-prezzi' || tab === 'articoli-prezzi' || tab === 'clienti-classi') && (
        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'articoli-prezzi' ? 'Cerca per codice articolo…' : 'Cerca per ragione sociale o codice cliente…'}
            data-testid={`pricing-search-${tab}`}
            className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(206,54%,20%)]/30"
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 rounded-full border-2 border-[hsl(206,54%,20%)] border-t-transparent animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          {tab === 'clienti-prezzi' && (
            clientiPrezziFiltered.length === 0 ? <EmptyState filtered={!!searchNorm} /> : (
              <table className="w-full text-sm" data-testid="pricing-table-clienti-prezzi">
                <thead>
                  <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                    <th className="w-8" />
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Cod. Cliente</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Ragione Sociale</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">N. Articoli</th>
                  </tr>
                </thead>
                <tbody>
                  {clientiPrezziPage.map((c) => {
                    const key = String(c.idCliente);
                    const isOpen = expanded.has(key);
                    return (
                      <React.Fragment key={key}>
                        <tr className="border-b hover:bg-secondary/40 transition-colors cursor-pointer" onClick={() => toggle(key)} data-testid={`pricing-row-cliente-${key}`}>
                          <td className="px-2 text-muted-foreground">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                          <td className="px-4 py-3 font-mono">{c.idCliente}</td>
                          <td className="px-4 py-3 font-medium">{c.ragioneSociale}</td>
                          <td className="px-4 py-3 text-right">{c.nArticoli}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-secondary/20">
                            <td />
                            <td colSpan={3} className="px-4 py-3">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="text-left py-1 font-medium">SKU</th>
                                    <th className="text-left py-1 font-medium">Descrizione</th>
                                    <th className="text-right py-1 font-medium">Prezzo</th>
                                    <th className="text-right py-1 font-medium">Valido dal</th>
                                    <th className="text-right py-1 font-medium">Valido al</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {c.articoli.map((a, i) => (
                                    <tr key={i} className="border-t border-dashed">
                                      <td className="py-1.5 font-mono">{a.sku}</td>
                                      <td className="py-1.5">{a.descrizione}</td>
                                      <td className="py-1.5 text-right font-semibold">{fmtPrice(a.prezzo)}</td>
                                      <td className="py-1.5 text-right text-muted-foreground">{fmtDate(a.validoDal)}</td>
                                      <td className="py-1.5 text-right text-muted-foreground">{fmtDate(a.validoAl)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
          {tab === 'clienti-prezzi' && clientiPrezziFiltered.length > 0 && (
            <Pagination page={pageSafe} pages={pages} isLoading={loading} onChange={setPage} testIdPrefix="pricing-clienti-prezzi" />
          )}

          {tab === 'articoli-prezzi' && (
            articoliPrezziFiltered.length === 0 ? <EmptyState filtered={!!searchNorm} /> : (
              <table className="w-full text-sm" data-testid="pricing-table-articoli-prezzi">
                <thead>
                  <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                    <th className="w-8" />
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">SKU</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Descrizione</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">N. Clienti</th>
                  </tr>
                </thead>
                <tbody>
                  {articoliPrezziPage.map((a) => {
                    const key = a.sku;
                    const isOpen = expanded.has(key);
                    return (
                      <React.Fragment key={key}>
                        <tr className="border-b hover:bg-secondary/40 transition-colors cursor-pointer" onClick={() => toggle(key)} data-testid={`pricing-row-articolo-${key}`}>
                          <td className="px-2 text-muted-foreground">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                          <td className="px-4 py-3 font-mono">{a.sku}</td>
                          <td className="px-4 py-3 font-medium">{a.descrizione}</td>
                          <td className="px-4 py-3 text-right">{a.nClienti}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-secondary/20">
                            <td />
                            <td colSpan={3} className="px-4 py-3">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="text-left py-1 font-medium">Cod. Cliente</th>
                                    <th className="text-left py-1 font-medium">Ragione Sociale</th>
                                    <th className="text-right py-1 font-medium">Prezzo</th>
                                    <th className="text-right py-1 font-medium">Valido dal</th>
                                    <th className="text-right py-1 font-medium">Valido al</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {a.clienti.map((cl, i) => (
                                    <tr key={i} className="border-t border-dashed">
                                      <td className="py-1.5 font-mono">{cl.idCliente}</td>
                                      <td className="py-1.5">{cl.ragioneSociale}</td>
                                      <td className="py-1.5 text-right font-semibold">{fmtPrice(cl.prezzo)}</td>
                                      <td className="py-1.5 text-right text-muted-foreground">{fmtDate(cl.validoDal)}</td>
                                      <td className="py-1.5 text-right text-muted-foreground">{fmtDate(cl.validoAl)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
          {tab === 'articoli-prezzi' && articoliPrezziFiltered.length > 0 && (
            <Pagination page={pageSafe} pages={pages} isLoading={loading} onChange={setPage} testIdPrefix="pricing-articoli-prezzi" />
          )}

          {tab === 'clienti-classi' && (
            clientiClassiFiltered.length === 0 ? <EmptyState filtered={!!searchNorm} /> : (
              <table className="w-full text-sm" data-testid="pricing-table-clienti-classi">
                <thead>
                  <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Cod. Cliente</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Ragione Sociale</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Sconto 1</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Sconto 2</th>
                  </tr>
                </thead>
                <tbody>
                  {clientiClassiPage.map((c) => (
                    <tr key={c.idCliente} className="border-b hover:bg-secondary/40 transition-colors" data-testid={`pricing-row-classe-${c.idCliente}`}>
                      <td className="px-4 py-3 font-mono">{c.idCliente}</td>
                      <td className="px-4 py-3 font-medium">{c.ragioneSociale}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtPct(c.sconto1)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtPct(c.sconto2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
          {tab === 'clienti-classi' && clientiClassiFiltered.length > 0 && (
            <Pagination page={pageSafe} pages={pages} isLoading={loading} onChange={setPage} testIdPrefix="pricing-clienti-classi" />
          )}

          {tab === 'articoli-scatola' && (
            articoliScatola.length === 0 ? <EmptyState /> : (
              <table className="w-full text-sm" data-testid="pricing-table-articoli-scatola">
                <thead>
                  <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">SKU</th>
                    <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Descrizione</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Sconto 1</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Sconto 2</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Qtà da</th>
                    <th className="text-right px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Validità</th>
                  </tr>
                </thead>
                <tbody>
                  {articoliScatolaPage.map((a) => (
                    <tr key={a.sku} className="border-b hover:bg-secondary/40 transition-colors" data-testid={`pricing-row-scatola-${a.sku}`}>
                      <td className="px-4 py-3 font-mono">{a.sku}</td>
                      <td className="px-4 py-3 font-medium">{a.descrizione}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtPct(a.sconto1)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtPct(a.sconto2)}</td>
                      <td className="px-4 py-3 text-right">{a.quantitaDa || '—'}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground text-xs">{fmtDate(a.validoDal)} — {fmtDate(a.validoAl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
          {tab === 'articoli-scatola' && articoliScatola.length > 0 && (
            <Pagination page={pageSafe} pages={pages} isLoading={loading} onChange={setPage} testIdPrefix="pricing-articoli-scatola" />
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ filtered }: { filtered?: boolean }) {
  return (
    <div className="p-12 text-center text-sm text-muted-foreground" data-testid="pricing-empty-state">
      {filtered ? 'Nessun risultato per la ricerca applicata.' : 'Nessun elemento trovato.'}
    </div>
  );
}
